import { BookingGateWayModeIdEnum, dateFormatDialog, FullBooking } from '@alluradirect/react';
import { Box, Hidden, Typography } from '@mui/material';
import Grid from 'components/core/Grid';
import { LabelAndValue } from 'components/core/LabelAndValue/LabelAndValue';
import LoadingSkeleton from 'components/core/LoadingSkeleton/LoadingSkeleton';
import { CheckoutSummaryFooterLastStep } from 'components/views/pages/Checkout/CheckoutSummary/components/CheckoutSummaryFooterLastStep';
import {
  balancePaymentDate,
  balancePaymentTotal,
  damageDepositAmount,
  hasBalancePayment,
} from 'components/views/pages/Checkout/CheckoutSummary/helpers';
import LockIcon from '@mui/icons-material/Lock';
import styled from 'styled-components';
import { amountDueToday } from 'components/views/pages/Checkout/helpers';
import { BookingCheckoutResponse } from 'components/views/pages/Checkout/hooks/useCheckout';
import dayjs from 'dayjs';
import { formatCanadianCurrency } from 'helpers/currencyHelpers';
import { AppDivider } from 'components/core/AppDivider/AppDivider';

interface Props {
  paymentType: string | undefined;
  checkoutData: BookingCheckoutResponse | undefined;
  bookingData: FullBooking | undefined;
  isLastStep: boolean;
}

export const SecureGrid = styled(Grid)`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  border: 1px solid ${props => props.theme.palette.grey[300]};
  border-radius: 5px;
  padding: ${props => props.theme.spacing(1)};

  & svg {
    margin-right: ${props => props.theme.spacing(1)};
    color: ${props => props.theme.palette.primary.main};
  }
`;

export const CheckoutSummaryFooter = ({ checkoutData, paymentType, isLastStep, bookingData }: Props) => {
  const dueToday = amountDueToday(checkoutData, paymentType);
  const dueLater = balancePaymentTotal(checkoutData);
  const dueLaterDate = balancePaymentDate(checkoutData);
  const damageDeposit = damageDepositAmount({ checkoutData, bookingData, isLastStep });

  const isBalancePayment = hasBalancePayment({ checkoutData, paymentType });

  const isGatewayClosed = checkoutData
    ? checkoutData.BOOKING.PROPERTY.GATEWAY_MODE.ID === BookingGateWayModeIdEnum.closed
    : false;

  if (isLastStep) {
    return <Box sx={{ width: '100%', marginTop: '24px' }}><CheckoutSummaryFooterLastStep bookingData={bookingData} /></Box>;
  }

  return (
    <>
      {
        (damageDeposit || isBalancePayment) ? (

          <Grid container spacing={1} >
            <Grid item xs={12}>
              <AppDivider marginVertical="16px" />
            </Grid>
            <Grid item xs={12}>
              <LoadingSkeleton isLoading={!checkoutData} scaleOneToOne>
                <LabelAndValue
                  label={isGatewayClosed ? 'Due on booking request approval (CAD)' : 'Due today (CAD)'}
                  value={formatCanadianCurrency(dueToday)}
                  variant="accounting"
                  fontSize={16}
                  fontWeight="initial"
                />
              </LoadingSkeleton>
            </Grid>
            <Grid item xs={12}>
              {isBalancePayment ? (
                <LabelAndValue
                  label={`Due by ${dateFormatDialog(dayjs(dueLaterDate))} (CAD)`}
                  value={formatCanadianCurrency(dueLater)}
                  variant="accounting"
                  fontSize={16}
                  fontWeight="initial"
                />
              ) : null}
            </Grid>
          </Grid >
        ) : null}
      <Hidden mdDown>
        <SecureGrid item xs={12} mt={2}>
          <LockIcon />
          <Typography variant="body2">Payment is 100% secure using SSL encryption</Typography>
        </SecureGrid>
      </Hidden>
    </>
  );
};
